/**
 * The travel charge of the Rules page (Battery and financing tab › Travel): the same arithmetic as the quote engine
 * (`app/design/quote/pricing.py`, `_travel`), for the page's live example, and the checks the server makes on save.
 * Quotes measure the straight-line distance from the nearest base × the road factor; nothing is looked up per quote.
 */
import type { S } from "../api/client";

export type Travel = S["TravelPricing"];
export type Hub = S["Hub"];
export type TravelBand = S["TravelBand"];

/** The most bases a company can enter (the contract's `maxItems`). */
export const MAX_BASES = 10;
/** Straight line → road: the default, explained on the page. */
export const ROAD_FACTOR = 1.25;

/** What a company starts from when it switches travel on: one empty base, nothing charged yet. */
export function startTravel(baseName: string): Travel {
  return { enabled: true, hubs: [{ name: baseName, address: "" }], per_km: 0, free_km: 0, road_factor: ROAD_FACTOR, bands: [] };
}

/** A base with a new address: its coordinates are the server's to find again on save. */
export function withAddress(hub: Hub, address: string): Hub {
  return { ...hub, address, place: null, lat: null, lng: null };
}

/** The road distance a quote uses for a house `straightKm` from the base. */
export function roadKm(travel: Travel, straightKm: number): number {
  return straightKm * travel.road_factor;
}

/** The engine's travel lines for a road distance and a system size: "Travel" (per km beyond the free distance plus
 * the distance band's flat fee) and "Accommodation" (null: none), and whether the quote asks for a manual quote. */
export function travelCharge(travel: Travel, road: number, kw: number): { travel: number; accommodation: number | null; manual: boolean } {
  if (travel.enabled === false) return { travel: 0, accommodation: null, manual: false };
  const perKm = Math.max(0, road - travel.free_km) * travel.per_km;
  const band = (travel.bands ?? []).find((b) => b.up_to_km == null || road <= b.up_to_km);
  let manual = travel.contact_beyond_km != null && road > travel.contact_beyond_km;
  let accommodation: number | null = null;
  const rule = travel.accommodation;
  if (rule && road > rule.min_km) {
    const size = rule.bands.find((b) => b.max_kw == null || kw <= b.max_kw);
    if (!size || size.contact_for_quote) manual = true;
    if (size?.amount) accommodation = size.amount;
  }
  return { travel: perKm + (band?.amount ?? 0), accommodation, manual };
}

/** Bands go from the nearest distance up, and only the last may have no limit (as the server checks). */
export function bandsInOrder(bounds: (number | null | undefined)[]): boolean {
  if (bounds.slice(0, -1).some((b) => b == null)) return false;
  const known = bounds.filter((b): b is number => b != null);
  return known.every((b, i) => b > 0 && (i === 0 || b > known[i - 1]!));
}

/** What keeps the page from saving: a base without its name or address, or bands out of order. */
export function travelProblems(travel: Travel | null | undefined): { bases: boolean; bands: boolean; accommodation: boolean } {
  if (!travel) return { bases: false, bands: false, accommodation: false };
  return {
    bases: travel.hubs.some((h) => !h.name.trim() || (!(h.address ?? "").trim() && (h.lat == null || h.lng == null))),
    bands: !bandsInOrder((travel.bands ?? []).map((b) => b.up_to_km)),
    accommodation: !!travel.accommodation && !bandsInOrder(travel.accommodation.bands.map((b) => b.max_kw)),
  };
}
